export const ONESIGNAL_APP_ID = 'c98b255b-706d-49be-90b7-ba3b9b2f9d33';

export const ONESIGNAL_ALLOWED_HOSTS = (
  import.meta.env.VITE_ONESIGNAL_ALLOWED_HOSTS ||
  'gym-saas-premium.vercel.app,localhost,127.0.0.1'
)
  .split(',')
  .map((h: string) => h.trim());

export type OneSignalState = 'pending' | 'ok' | 'failed' | 'unsupported-host';

let oneSignalState: OneSignalState = 'pending';

export const getOneSignalState = (): OneSignalState => oneSignalState;

export const setOneSignalState = (state: OneSignalState): void => {
  oneSignalState = state;
};

export const isOneSignalReady = (): boolean => oneSignalState === 'ok';

export const isHostAllowed = (): boolean => {
  if (typeof window === 'undefined') return false;
  return ONESIGNAL_ALLOWED_HOSTS.includes(window.location.hostname);
};

export type OneSignalLike = {
  init: (config: Record<string, any>) => Promise<any>;
  login: (externalId: string) => Promise<any>;
  logout: () => Promise<any>;
  User?: {
    addTag: (key: string, value: string) => Promise<any>;
    removeTag: (key: string) => Promise<any>;
    PushSubscription?: {
      optIn: () => Promise<any>;
      refresh: () => Promise<any>;
    };
  };
  Notifications?: {
    requestPermission: () => Promise<boolean>;
    permissionNative?: NotificationPermission;
  };
};

declare global {
  interface Window {
    OneSignal?: OneSignalLike;
    OneSignalDeferred?: Array<(OneSignal: OneSignalLike) => void | Promise<void>>;
  }
}

const withTimeout = <T>(promise: Promise<T>, ms: number, label = 'operation'): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error(`OneSignal ${label} timed out after ${ms}ms`)), ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

export const requestPushPermission = async (
  timeoutMs = 12000
): Promise<NotificationPermission> => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  const OneSignal = window.OneSignal;

  const useOneSignal =
    isOneSignalReady() &&
    OneSignal?.Notifications?.requestPermission;

  try {
    if (useOneSignal) {
      const granted = await withTimeout(
        OneSignal!.Notifications!.requestPermission(),
        timeoutMs,
        'requestPermission'
      );
      const finalPerm =
        OneSignal?.Notifications?.permissionNative ??
        (granted ? 'granted' : Notification.permission);
      return finalPerm;
    }

    const nativePerm = await withTimeout(
      Notification.requestPermission(),
      timeoutMs,
      'native requestPermission'
    );
    return nativePerm;
  } catch (error) {
    console.warn('[onesignal] requestPermission failed, falling back to native', error);
    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }
};

/**
 * Push notifications (new subscriptions / renewals / new gyms) are only for
 * gym admins and super admins. Any other role must never be prompted nor
 * subscribed.
 */
export const PUSH_ADMIN_ROLES = ['gym_admin', 'super_admin'] as const;

export const isPushAdminRole = (role?: string): boolean =>
  !!role && (PUSH_ADMIN_ROLES as readonly string[]).includes(role);

export type OneSignalUser = {
  user_id: string;
  role?: string;
  gym_id?: string;
};

const safeAwait = (p: any, label: string) => {
  return Promise.resolve(p).catch((e: any) => console.error(`[onesignal] ${label}`, e));
};

// Pending desired identity. The SDK may not be ready yet when React syncs the
// user, so we keep it here and apply it once init completes.
let pendingUser: OneSignalUser | null = null;

const canPrompt = (): boolean =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  Notification.permission === 'granted';

const applyOneSignalUser = async (OneSignal: OneSignalLike): Promise<void> => {
  const user = pendingUser;

  if (!user?.user_id || !isPushAdminRole(user.role)) {
    await safeAwait(OneSignal.logout?.(), 'logout');
    await safeAwait(OneSignal.User?.removeTag?.('role'), 'removeTag role');
    await safeAwait(OneSignal.User?.removeTag?.('gym_id'), 'removeTag gym_id');
    return;
  }

  try {
    await safeAwait(OneSignal.login(user.user_id), `login failed for ${user.user_id}`);
    await safeAwait(OneSignal.User?.addTag('role', user.role as string), 'addTag role');
    if (user.gym_id) {
      await safeAwait(OneSignal.User?.addTag('gym_id', user.gym_id), 'addTag gym_id');
    } else {
      await safeAwait(OneSignal.User?.removeTag?.('gym_id'), 'removeTag gym_id');
    }
    // optIn() shows the permission prompt when there is no valid token, so it
    // must only run once the admin explicitly granted permission (Settings).
    if (canPrompt()) {
      await safeAwait(OneSignal.User?.PushSubscription?.optIn?.(), 'PushSubscription.optIn');
      await safeAwait(OneSignal.User?.PushSubscription?.refresh?.(), 'PushSubscription.refresh');
    }
  } catch (e) {
    console.error('[onesignal] sync user failed', e);
  }
};

/**
 * Mark the current OneSignal user (external id + role/gym_id tags) after a
 * successful init. Non-admin roles are logged out instead of subscribed.
 * No-op if init failed or the host is not allowed.
 */
export const syncOneSignalUser = async (
  user: OneSignalUser | null | undefined
): Promise<void> => {
  pendingUser = user ?? null;
  if (!isOneSignalReady()) return;
  const OneSignal = window.OneSignal;
  if (!OneSignal) return;
  await applyOneSignalUser(OneSignal);
};

let initStarted = false;

export const initOneSignalSdk = async (): Promise<void> => {
  if (initStarted || oneSignalState === 'ok') return;

  const hostname = window.location.hostname;
  if (!isHostAllowed()) {
    console.warn(`OneSignal: Dominio "${hostname}" no autorizado para inicialización`);
    setOneSignalState('unsupported-host');
    return;
  }

  initStarted = true;

  if (!document.getElementById('onesignal-sdk')) {
    const script = document.createElement('script');
    script.id = 'onesignal-sdk';
    script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
    script.defer = true;
    document.head.appendChild(script);
  }

  const OneSignalDeferred = (window.OneSignalDeferred = window.OneSignalDeferred || []);

  OneSignalDeferred.push(async (OneSignal: OneSignalLike) => {
    try {
      await OneSignal.init({
        appId: ONESIGNAL_APP_ID,
        serviceWorkerPath: '/OneSignalSDKWorker.js',
        serviceWorkerParam: { scope: '/' },
        allowLocalhostAsSecureOrigin: true,
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: 'push',
                // Auto prompt stays disabled: the only trigger is the
                // "Activar" button in Settings (gym admins / super admins).
                autoPrompt: false,
                text: {
                  actionMessage:
                    'Recibe notificaciones de nuevas suscripciones y renovaciones en tu gimnasio.',
                  acceptButton: 'Activar',
                  cancelButton: 'No, gracias',
                },
              },
            ],
          },
        },
      });
      setOneSignalState('ok');
      // The user may have been synced before the SDK finished initializing.
      await applyOneSignalUser(OneSignal);
    } catch (error: any) {
      if (error?.message?.includes('gym-saas-premium.vercel.app')) {
        console.warn('OneSignal: Error de dominio (esperado en dev)');
      } else {
        console.error('OneSignal Init Promise Error:', error);
      }
      setOneSignalState('failed');
      initStarted = false;
    }
  });
};